import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Firm settings → Assessment defaults (T-0219 / E20-F01, ADR-022).
 *
 *   GET /api/v1/firm/assessment-defaults   (firm admin)
 *   PUT /api/v1/firm/assessment-defaults   (firm admin; full replace)
 *
 * What the engagement methodology used to carry: the default health domains,
 * the readiness criteria with their weights and Go / Conditional thresholds,
 * the finding types and the checkpoint scoring scale. Every new engagement
 * receives them at creation; nothing locks or gates on them.
 */

export const DefaultDomainSchema = z.object({
  name: z.string(),
  description: z.string().nullable(),
});
export type DefaultDomain = z.infer<typeof DefaultDomainSchema>;

export const CriterionScoreTypeSchema = z.enum(['numeric_0_10', 'pass_fail', 'ryg']);
export type CriterionScoreType = z.infer<typeof CriterionScoreTypeSchema>;

export const DefaultCriterionSchema = z.object({
  category: z.string(),
  name: z.string(),
  weight: z.number(),
  score_type: CriterionScoreTypeSchema,
  /** T-0238: what 100%, 50% and 0% look like; the AI scores against it. */
  description: z.string().nullable().optional(),
});
export type DefaultCriterion = z.infer<typeof DefaultCriterionSchema>;

export const DefaultThresholdsSchema = z.object({
  go: z.number(),
  conditional: z.number(),
});
export type DefaultThresholds = z.infer<typeof DefaultThresholdsSchema>;

export const DefaultFindingTypesSchema = z.object({
  categories: z.array(z.string()),
  severities: z.array(z.string()),
});
export type DefaultFindingTypes = z.infer<typeof DefaultFindingTypesSchema>;

export const DefaultScoringScaleSchema = z.object({
  score_types: z.array(z.string()),
  numeric: z.object({
    min: z.number(),
    max: z.number(),
    pass_at: z.number(),
    divergent_at: z.number(),
  }),
  pass_fail: z.array(z.string()),
  ryg: z.array(z.string()),
});
export type DefaultScoringScale = z.infer<typeof DefaultScoringScaleSchema>;

export const AssessmentDefaultsValueSchema = z.object({
  domains: z.array(DefaultDomainSchema),
  criteria: z.array(DefaultCriterionSchema),
  thresholds: DefaultThresholdsSchema,
  finding_types: DefaultFindingTypesSchema,
  scoring_scale: DefaultScoringScaleSchema,
});
export type AssessmentDefaultsValue = z.infer<typeof AssessmentDefaultsValueSchema>;

export const AssessmentDefaultsSchema = AssessmentDefaultsValueSchema.extend({
  version: z.number(),
  updated_at: z.string().nullable(),
  updated_by_user_id: z.string().nullable(),
  updated_by_name: z.string().nullable(),
});
export type AssessmentDefaults = z.infer<typeof AssessmentDefaultsSchema>;

export const ASSESSMENT_DEFAULTS_PATH = '/api/v1/firm/assessment-defaults';
export const ASSESSMENT_DEFAULTS_KEY = ['assessment-defaults'] as const;

/** Weights must sum to 1 within this tolerance (the API applies the same). */
export const WEIGHT_SUM_TOLERANCE = 0.001;

/** Σ weights of a criteria set, rounded to 4 places so 0.08 × 13 reads as 1. */
export function weightsSum(criteria: ReadonlyArray<{ weight: number }>): number {
  return Math.round(criteria.reduce((acc, c) => acc + c.weight, 0) * 10_000) / 10_000;
}

export function weightsSumToOne(criteria: ReadonlyArray<{ weight: number }>): boolean {
  return Math.abs(weightsSum(criteria) - 1) <= WEIGHT_SUM_TOLERANCE;
}

export function getAssessmentDefaults(): Promise<AssessmentDefaults> {
  return apiClient(ASSESSMENT_DEFAULTS_PATH, AssessmentDefaultsSchema);
}

export function putAssessmentDefaults(value: AssessmentDefaultsValue): Promise<AssessmentDefaults> {
  return apiClient(ASSESSMENT_DEFAULTS_PATH, AssessmentDefaultsSchema, {
    method: 'PUT',
    body: value,
  });
}

export function useAssessmentDefaultsQuery() {
  return useQuery({
    queryKey: ASSESSMENT_DEFAULTS_KEY,
    queryFn: getAssessmentDefaults,
  });
}

export function useSaveAssessmentDefaultsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: putAssessmentDefaults,
    onSuccess: (saved) => {
      qc.setQueryData(ASSESSMENT_DEFAULTS_KEY, saved);
    },
  });
}
